<template>
  <div v-if="resultCategory.total > 0">
    <section class="d-flex justify-content-between align-items-center mb-2 mt-4">
      <div class="category-title">
        <h2 class="mb-0 t-primary position-relative d-inline-block">
          <span class="pr-2">{{ getIndexAsString(activeIndex) }}</span>
          <span class="t-secondary">{{ resultCategory.total | formatNumber }}</span>
          <i v-if="isScopedSearch && totalSuggestionResults > 0" class="fa fa-info-circle t-secondary t-xxs ml-2" v-tooltip="tooltip" />
          <i v-if="activeIndex === 'news' && !$can('viewAllNewsResults') && resultCategory.total > 0" class="fa fa-info-circle t-secondary t-xxs ml-2" v-tooltip="'Showing news results of the last 7 days. Full AcademicLabs access is required to view all news since 2023.'" />
        </h2>
      </div>

      <div class="category-actions">
        <button
          type="button"
          v-if="showAiAnalysis"
          v-tooltip="'Ask our AI anything! Extract insights from a selection of your search results'"
          @click="openAiAnalysis"
          class="btn btn-sm btn-primary--yellow t-primary px-2 ml-2 d-flex align-items-center"
        >
          <i class="fa al-icon-stars mr-2 pb-1" />
          <span class="t-600">AI</span>
        </button>

        <template v-if="canSaveResultsToBookmarks">
          <bookmark-folder
            v-if="$can('bulkBookmarkSave')"
            class="ml-2"
            :scope="activeIndex"
            isFromSearch
            :state="state"
            isBulkSave
            :count="resultCategory.total"
            :isScopedSearch="isScopedSearch"
            :tooltip="'Save first 500 results to a bookmark folder'"
          />
          <button
            type="button"
            v-else-if="!isScopedSearch"
            @click="$can.notAllowed('bookmark')"
            class="btn btn-sm btn-primary px-3 ml-2"
          >
            <i class="fa fa-bookmark" />
            <span class="px-2 t-600">Save</span>
            <i class="fa fa-lock semi-transparent" />
          </button>
        </template>

        <export
          :index="activeIndex"
          :isScopedSearch="isScopedSearch"
          :state="state"
          :count="resultCategory.total"
        />
      </div>

    </section>
    <section class="d-flex justify-content-between align-items-center mb-2">
      <div class="t-sm t-600 t-grey text-nowrap d-flex flex-wrap flex-row">
        <span v-if="activeIndex === 'news'" class="mr-4">Content from <a href="https://www.prnewswire.com/" target="_blank">PR Newswire</a> & <a href="https://www.fiercebiotech.com/" target="_blank">Fierce</a></span>
        <span v-if="showResultCount">Showing results {{ resultStart }} - {{ resultEnd }}</span>
      </div>
      <div class="d-flex align-items-center justify-content-end flex-wrap">
        <compare-button
          v-if="isBlockView && (activeIndex === 'researchers' || activeIndex === 'research_groups')"
          :options="compareOptions"
          @selected="handleCompare"
        />

        <sorting
          class="ml-4"
          :sorts="orderedSorts"
          @sortHandled="handleSort"
        />

        <search-view-toggler
          v-if="!fixedResultComponent"
          :activeIndex="activeIndex"
        />
      </div>
    </section>

    <template v-if="fixedResultComponent">
      <div class="row">
        <div class="col-12 mb-2" v-for="(result, i) in resultCategory.results" :key="`${activeIndex}-${i}`">
          <component :is="fixedResultComponent"
            :index="activeIndex"
            :result="result"
          />
        </div>
      </div>
    </template>

    <template v-else>

      <div class="row" :class="{ 'restore-padding': isBlockView }">
        <div v-for="(result, j) in resultCategory.results" :key="`${activeIndex}-${j}`"
          :class="{ 'col-md-6 col-xl-4': isBlockView }" class="list-view col-12">
            <component :is="componentName"
              :index="activeIndex"
              :result="result"
            />
        </div>

        <div v-for="(result, k) in resultCategory.suggestion_results" :key="`suggestion_${activeIndex}-${k}`"
          :class="{ 'col-md-6 col-xl-4': isBlockView }" class="list-view col-12">
            <component :is="componentName"
              :index="activeIndex"
              :result="result"
              class="card-light-overlap"
            />
        </div>

        <template v-if="lockedResultPlaceholders">
          <template v-if="isBlockView">
            <div v-for="n in (lockedResultPlaceholders - (resultCategory.results.length) - (resultCategory.suggestion_results.length))" :key="`phl_${n}`"
              @click="$can.notAllowed()"
              class="list-view col-12 col-md-6 col-xl-4 cursor-pointer">
              <img v-if="activeIndex === 'researchers' || activeIndex === 'research_groups'"
                :src="require('@/assets/img/resultcard_graphs_locked.svg')">
              <img v-else
                :src="require('@/assets/img/resultcard_locked.svg')">
            </div>
          </template>
          <template v-else>
            <div
              v-if="!resultCategory.results.length && !resultCategory.suggestion_results.length"
              @click="$can.notAllowed()"
              class="list-view col-12 cursor-pointer">
              <img :src="require('@/assets/img/resultlist_locked.svg')">
            </div>
          </template>

        </template>
      </div>
    </template>
  </div>
</template>

<script>
import MPService from '@/services/mixpanel'
import { mapActions, mapGetters } from 'vuex'

import ResultBaseBlock from '@/components/search/results/BaseBlock'
import ResultBaseList from '@/components/search/results/BaseList'
import Sorting from '@/components/search/Sorting'
import CompareButton from '@/components/search/CompareButton'
import Export from '@/components/search/Export'
import SearchViewToggler from '@/components/search/SearchViewToggler'
import BookmarkFolder from '@/components/bookmark_folder/Show'

// Possible fixedSearchResultsComponents
import WebsiteMatches from '@/components/search/results/fixedResultComponents/WebsiteMatches'

export default {
  name: 'searchBaseCategories',
  components: {
    Sorting,
    Export,
    ResultBaseBlock,
    ResultBaseList,
    BookmarkFolder,
    CompareButton,
    SearchViewToggler,
    WebsiteMatches
  },
  props: {
    state: {
      type: Object,
      required: true
    },
    isScopedSearch: {
      type: Boolean,
      required: true
    },
    searchResults: {
      type: Object,
      required: true
    },
    itemsPerPage: {
      type: Number,
      required: true
    },
    page: {
      type: Number,
      required: true
    },
    showResultCount: {
      type: Boolean,
      required: true
    },
    lockedResultPlaceholders: {
      type: Number,
      required: true
    },
    // Override normal result component logic with a fixed component, don't forget to register it here as well though.
    fixedResultComponent: {
      type: String,
      required: false,
      default: ''
    },
    // For when we don't want anyone to bookmark these results
    canSaveResultsToBookmarks: {
      type: Boolean,
      required: false,
      default: true
    }
  },
  data () {
    return {
      resultCategory: {},
      activeIndex: '',
      compareOptions: [{
        label: 'Publications & Citations Histograms',
        value: 'graph',
        active: true
      }, {
        label: 'Expertise (publications of last 5 years)',
        value: 'expertise',
        active: false,
        locked: !this.$can('compareExpertise')
      }]
    }
  },
  mounted () {
    this.activeIndex = Object.keys(this.searchResults.categories)[0]
    this.resultCategory = this.searchResults.categories[this.activeIndex]
    this.compareOptions = this.compareOptions.map(option => {
      if (option.value === this.comparison) option.active = true
      else option.active = false
      return option
    })
  },
  computed: {
    showAiAnalysis () {
      const enabledIndices = [
        'research_topics',
        'publications',
        'clinical_trials',
        'projects',
        'patents',
        'news'
      ]
      if (
        this.$can('promptAI') &&
        enabledIndices.indexOf(this.activeIndex) !== -1
      ) {
        return true
      }
      return false
    },
    isBlockView () {
      return this.searchView(this.activeIndex) !== 'list'
    },
    componentName () {
      return this.isBlockView ? `ResultBaseBlock` : `ResultBaseList`
    },
    resultStart () {
      return this.itemsPerPage * (this.page - 1) + 1
    },
    resultEnd () {
      // Trick to send parameters along with a computed property
      return Math.min(this.itemsPerPage * this.page, this.resultCategory.total)
    },
    orderedSorts () {
      if (!this.resultCategory || !this.resultCategory.sorts) return []
      return Array.from(this.resultCategory.sorts).sort((a, b) => a.order - b.order)
    },
    totalSuggestionResults () {
      return this.resultCategory.total_suggestion_results
    },
    tooltip () {
      if (this.resultCategory.total_suggestion_results === 0) {
        return 'All results are verified.'
      }
      // Kind of hacky to get the correct tooltips, but does the job for now. Should move this to a seperate component indeally
      if (this.$route.name === 'research_group.explore') {
        return `${this.resultCategory.total_suggestion_results} of these results were found based on the names of this group’s researchers. These are not yet verified and could belong to someone with an identical name.`
      } else if (this.$route.name === 'organisation.explore') {
        return `${this.resultCategory.total_suggestion_results} of these results were found based on the organisation's name. These are not yet verified and could belong to another organisation with an identical name.`
      } else {
        return `${this.resultCategory.total_suggestion_results} of these results were found based on the researcher's name. These are not yet verified and could belong to someone with an identical name.`
      }
    },
    ...mapGetters({
      comparison: 'search/comparison'
    })
  },
  methods: {
    ...mapActions('search', [
      'setCompare'
    ]),
    handleSort (name) {
      this.$emit('sortHandled', name)
    },
    handleCompare (compare) {
      if (!this.$can('compareExpertise')) {
        this.$can.notAllowed()
        return
      }
      // Method that allows users to compare expertises
      this.setCompare(compare)
      MPService.trackCompareUsed(compare, this.state.index)
      this.compareOptions = this.compareOptions.map(option => {
        if (option.value === compare) option.active = true
        else option.active = false
        return option
      })
    },
    openAiAnalysis () {
      document.activeElement.blur()
      this.$events.$emit('modal:ai_analysis:open', {
        count: this.resultCategory.total,
        state: this.state
      })
    }
  }
}
</script>
